import React from "react";
import { Box, Container, Typography, Paper, Divider } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ArrowBack, Gavel, Description, Warning } from "@mui/icons-material";

const TermsAndConditions = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: "1. Acceptance of Terms",
      content: `By accessing or using the Orunal platform ("Service"), you agree to be bound by these Terms and Conditions ("Terms"). If you disagree with any part of these terms, you may not access the Service.

Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms. These Terms apply to all visitors, users, and others who access or use the Service.`,
    },
    {
      title: "2. Description of Service",
      content: `Orunal provides a comprehensive task management and organizational platform designed to help businesses and teams:

• Manage tasks, projects, and workflows
• Track employee activities and performance
• Facilitate team collaboration and communication
• Manage human resources functions including leave management
• Generate reports and analytics
• Store and manage organizational data

We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time without prior notice.`,
    },
    {
      title: "3. User Accounts and Registration",
      content: `To access certain features of the Service, you must register for an account. When you register, you agree to:

• Provide accurate, current, and complete information
• Maintain and promptly update your account information
• Maintain the security of your password and account
• Accept responsibility for all activities under your account
• Notify us immediately of any unauthorized use of your account

You are responsible for safeguarding your account credentials. We cannot and will not be liable for any loss or damage arising from your failure to comply with this security obligation.`,
    },
    {
      title: "4. User Responsibilities and Conduct",
      content: `You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree NOT to:

• Violate any applicable laws or regulations
• Infringe upon the rights of others
• Transmit any harmful code, viruses, or malicious software
• Attempt to gain unauthorized access to any part of the Service
• Interfere with or disrupt the Service or servers
• Use the Service to harass, abuse, or harm another person
• Impersonate any person or entity
• Collect or store personal data about other users without consent
• Use automated systems to access the Service without permission
• Reverse engineer or attempt to extract the source code of the Service`,
    },
    {
      title: "5. Intellectual Property Rights",
      content: `The Service and its original content, features, and functionality are owned by Orunal Technologies Private Limited and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.

• Our trademarks and trade dress may not be used without prior written consent
• You may not copy, modify, distribute, sell, or lease any part of our Service
• You may not reverse engineer or attempt to extract the source code
• All feedback, comments, or suggestions you provide may be used without obligation to you

You retain all rights to the data and content you upload to the Service. By uploading content, you grant us a license to use, store, and display that content as necessary to provide the Service.`,
    },
    {
      title: "6. Data Privacy and Security",
      content: `Your use of the Service is also governed by our Privacy Policy. Please review our Privacy Policy to understand our practices regarding your data.

We implement reasonable security measures to protect your information. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.

You are responsible for:
• Maintaining the confidentiality of your account credentials
• Ensuring the security of data you upload to the Service
• Complying with applicable data protection laws
• Obtaining necessary consents for employee data processing`,
    },
    {
      title: "7. Payment Terms and Subscription",
      content: `Certain features of the Service may require payment. By purchasing a subscription, you agree to:

• Pay all fees according to the pricing and payment terms in effect
• Provide current, complete, and accurate billing information
• Promptly update account information in case of changes
• Authorize us to charge your payment method for applicable fees

Subscription fees are non-refundable except as required by law. We reserve the right to change our pricing with 30 days' notice. Your continued use after price changes constitutes acceptance of the new fees.`,
    },
    {
      title: "8. Service Availability and Modifications",
      content: `We strive to provide reliable and continuous service, but we do not guarantee:

• Uninterrupted or error-free operation of the Service
• That defects will be corrected immediately
• That the Service will meet your specific requirements
• That data will never be lost or corrupted

We may perform scheduled maintenance with advance notice. We reserve the right to modify or discontinue any feature of the Service at any time. We will not be liable to you or any third party for any modification, suspension, or discontinuance of the Service.`,
    },
    {
      title: "9. Limitation of Liability",
      content: `To the maximum extent permitted by law, Orunal Technologies Private Limited shall not be liable for:

• Any indirect, incidental, special, consequential, or punitive damages
• Loss of profits, revenue, data, or use
• Service interruptions or data loss
• Errors or inaccuracies in content
• Unauthorized access to your data
• Third-party conduct or content

Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim, or $100, whichever is greater.`,
    },
    {
      title: "10. Indemnification",
      content: `You agree to indemnify, defend, and hold harmless Orunal Technologies Private Limited, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses, including reasonable attorney fees, arising out of or in any way connected with:

• Your access to or use of the Service
• Your violation of these Terms
• Your violation of any third-party rights
• Any content you submit or transmit through the Service`,
    },
    {
      title: "11. Termination",
      content: `We may terminate or suspend your account and access to the Service immediately, without prior notice, for any reason, including:

• Breach of these Terms
• Fraudulent, illegal, or harmful activities
• Extended periods of inactivity
• Request by law enforcement or government agencies

Upon termination:
• Your right to use the Service will immediately cease
• We may delete your account and all associated data
• You remain liable for all obligations accrued prior to termination

Provisions that should survive termination will continue to apply, including intellectual property rights, disclaimers, and limitations of liability.`,
    },
    {
      title: "12. Dispute Resolution and Governing Law",
      content: `These Terms shall be governed by and construed in accordance with the laws of India, without regard to conflict of law provisions.

Any disputes arising out of or relating to these Terms or the Service shall be resolved through:

1. Good faith negotiations between the parties
2. If negotiations fail, binding arbitration in accordance with the Arbitration and Conciliation Act, 1996
3. Arbitration shall take place in [City], India
4. The arbitration shall be conducted in English

Nothing in this section prevents either party from seeking injunctive relief in court to protect intellectual property rights.`,
    },
    {
      title: "13. Changes to Terms",
      content: `We reserve the right to modify or replace these Terms at any time at our sole discretion. We will provide notice of any material changes by:

• Posting the new Terms on this page
• Updating the "Last Updated" date
• Sending an email notification to your registered email address (for material changes)

Your continued use of the Service after any changes constitutes acceptance of the new Terms. You are responsible for reviewing these Terms periodically.`,
    },
    {
      title: "14. Miscellaneous",
      content: `Severability: If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full effect.

Waiver: No waiver of any term shall be deemed a further or continuing waiver of such term or any other term.

Entire Agreement: These Terms constitute the entire agreement between you and Orunal regarding the Service.

Assignment: You may not assign or transfer these Terms without our prior written consent. We may assign our rights and obligations without restriction.

Force Majeure: We shall not be liable for any failure to perform due to causes beyond our reasonable control.`,
    },
    {
      title: "15. Contact Information",
      content: `If you have any questions about these Terms and Conditions, please contact us:

Email: legal@orunal.com
Support: support@orunal.com
Address: Orunal Technologies Private Limited

For general inquiries, please visit our website or use the contact form provided in the application.`,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
        py: 6,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mb: 3,
            background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            color: "white",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <ArrowBack
              sx={{ mr: 2, cursor: "pointer", fontSize: 28 }}
              onClick={() => navigate("/")}
            />
            <Gavel sx={{ mr: 2, fontSize: 36 }} />
            <Typography variant="h3" component="h1" fontWeight="bold">
              Terms and Conditions
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Last Updated: February 9, 2026
          </Typography>
          <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.3)" }} />
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Welcome to Orunal. These Terms and Conditions govern your use of our
            platform and services. Please read them carefully before using our
            Service. By accessing or using Orunal, you agree to be bound by
            these terms.
          </Typography>
        </Paper>

        {/* Content Sections */}
        {sections.map((section, index) => (
          <Paper
            key={index}
            elevation={2}
            sx={{
              p: 4,
              mb: 3,
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: 6,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
              <Description sx={{ mr: 2, color: "#f5576c", mt: 0.5 }} />
              <Typography
                variant="h5"
                component="h2"
                fontWeight="bold"
                sx={{ color: "#f5576c" }}
              >
                {section.title}
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: "text.secondary",
                whiteSpace: "pre-line",
              }}
            >
              {section.content}
            </Typography>
          </Paper>
        ))}

        {/* Footer Warning */}
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mt: 4,
            backgroundColor: "#fff3cd",
            border: "2px solid #ffc107",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Warning sx={{ mr: 1, color: "#ff6f00" }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: "#ff6f00" }}>
              Legal Agreement
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            These Terms and Conditions constitute a legally binding agreement
            between you and Orunal Technologies Private Limited. By using our
            Service, you acknowledge that you have read, understood, and agree
            to be bound by these terms. If you do not agree to these terms,
            please do not use our Service.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsAndConditions;
